import { ListApiResponse } from '@ianesthesie-kit/services';
import { SentryService } from '@ianesthesie-kit/sentry';
import { SnomedCode, SnomedItem } from './snomed.model';
import { captureThrowOnDev, isEmpty, isNotEmpty } from '@/util';
import type { Emphasis } from '@/modules/surgicalEvent';
import axios from 'axios';
import { stringify } from 'query-string';
import { AvailableLocale, languageMap } from '@ianesthesie-kit/config';

type InternalSnomedItem = {
  code: string;
  displayNameEn: string;
  displayNameFr: string;
  emphasis?: Emphasis;
};

export class SnomedService {
  private static readonly INSTANCE = new SnomedService();

  private static readonly CHUNK_SIZE = 100;

  private readonly cache: Record<SnomedCode, InternalSnomedItem>;

  private readonly pendingCodes = new Set<SnomedCode>();

  private flushPromise: Promise<void> | null = null;

  private constructor() {
    this.cache = {};
  }

  public static getInstance() {
    return this.INSTANCE;
  }

  public getCodeTranslation(
    snomed: SnomedItem | SnomedItem['code'],
    locale: string
  ): string | undefined {
    if (!snomed || !locale) {
      return undefined;
    }

    if (locale !== 'en' && locale !== 'fr') {
      captureThrowOnDev(`Unknown locale: ${locale}`);
      return typeof snomed === 'object' ? snomed.name : snomed;
    }

    const code = typeof snomed === 'object' ? snomed.code : snomed;
    const translationKey = this.getNameKey(locale);
    const translation = this.cache[code]?.[translationKey] ?? '';
    if (translation) {
      return translation;
    }

    SentryService.getInstance().captureException(
      new Error(`Snomed translation for ${code} was not present in cache`)
    );

    return undefined;
  }

  public async search(
    loc: string,
    lim: number,
    searchKey: string
  ): Promise<SnomedItem[]> {
    const qs = stringify({
      searchKey,
      language: languageMap[loc as AvailableLocale],
      'page-size': lim
    });
    return axios
      .get<ListApiResponse<InternalSnomedItem>>(`/bff/snomed?${qs}`)
      .then((res) =>
        res.data.data.map((item) => ({
          code: item.code,
          name: item[this.getNameKey(loc)],
          emphasis: item.emphasis
        }))
      );
  }

  public async findByCode(
    loc: string,
    code: string | string[]
  ): Promise<SnomedItem[]> {
    if (isEmpty(code)) {
      return [];
    }

    const codes = Array.isArray(code) ? code : [code];

    await this.updateCacheIfNeeded(codes);

    return codes.map((c) => {
      const item = this.cache[c];
      return {
        code: item?.code ?? c,
        name: item?.[this.getNameKey(loc)] ?? c,
        emphasis: item?.emphasis
      };
    });
  }

  public async cacheSnomedItems<T extends { snomedCode?: SnomedCode }>(
    items: T[] | string[]
  ) {
    let codes: string[] = [];
    if (isNotEmpty(items)) {
      if (typeof items[0] === 'string') {
        codes = items as string[];
      } else {
        codes = (items as T[])
          .map((it) => it.snomedCode)
          .filter(Boolean) as string[];
      }
    }

    await this.updateCacheIfNeeded(codes);
  }

  // Coalesces concurrent code lookups (e.g. one per rendered chip) into a
  // single batched request, flushed on a microtask. All callers within the
  // same tick share one flush promise and resolve once the cache is populated.
  // A resolved-promise continuation is used (rather than queueMicrotask) for
  // broader browser support with identical microtask timing.
  private updateCacheIfNeeded(code: string | string[]): Promise<void> {
    const codes = (Array.isArray(code) ? code : [code]).filter(
      (c) => !(c in this.cache)
    );
    if (isEmpty(codes)) {
      return Promise.resolve();
    }

    codes.forEach((c) => this.pendingCodes.add(c));

    if (!this.flushPromise) {
      this.flushPromise = Promise.resolve().then(() => {
        const batch = Array.from(this.pendingCodes);
        this.pendingCodes.clear();
        this.flushPromise = null;
        return this.fetchAndCache(batch);
      });
    }

    return this.flushPromise;
  }

  private async fetchAndCache(codes: string[]): Promise<void> {
    const notCachedCodes = codes.filter((c) => !(c in this.cache));
    if (isEmpty(notCachedCodes)) {
      return;
    }

    const codeChunkPromises: Promise<void>[] = [];
    for (let i = 0; i < notCachedCodes.length; i += SnomedService.CHUNK_SIZE) {
      const codesChunk = notCachedCodes.slice(i, i + SnomedService.CHUNK_SIZE);
      const qs = stringify({
        code: codesChunk
      });
      codeChunkPromises.push(
        axios
          .get<ListApiResponse<InternalSnomedItem>>(`/bff/snomed?${qs}`)
          .then((r) => r.data.data)
          .then((items) => {
            items.forEach((item) => {
              this.cache[item.code] = item;
            });
          })
      );
    }
    await Promise.allSettled(codeChunkPromises);
  }

  private getNameKey(
    locale: string
  ): keyof Omit<InternalSnomedItem, 'code' | 'emphasis'> {
    if (locale === 'en') {
      return 'displayNameEn';
    }

    if (locale === 'fr') {
      return 'displayNameFr';
    }

    captureThrowOnDev(`Snomed names for locale ${locale} is not available`);
    return 'displayNameEn';
  }
}
